import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { demoComments } from "@/mocks/comments";
import { usePageMeta } from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";

export default function Detail() {
  const { id } = useParams();
  const [toast, setToast] = useState("");
  const movie = movies.find((m) => m.id === id);

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id)
      .map((m) => {
        let score = 0;
        if (m.type === movie.type) score += 3;
        if (m.category === movie.category) score += 2;
        if (m.tags.some((t) => movie.tags.includes(t))) score += 2;
        if (m.actors.some((a) => movie.actors.includes(a))) score += 3;
        return { movie: m, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((item) => item.movie);
  }, [movie]);

  usePageMeta({
    title: movie
      ? `${movie.title} - 详细介绍 | 国产精品久久`
      : "影视作品详情 | 国产精品久久",
    description: movie
      ? `${movie.title}是一部${movie.year}年上映的国产${movie.type}作品，由${movie.director}执导，${movie.actors.join("、")}主演。${movie.summary}`
      : "国产精品久久影视作品详情页。",
    keywords: movie ? `${movie.title},${movie.type},${movie.actors.join(",")},国产影视` : "国产影视",
  });

  const share = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，快去分享给朋友吧");
    } catch {
      setToast(`复制失败，请手动复制：${url}`);
    }
    setTimeout(() => setToast(""), 2500);
  };

  if (!movie) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-film-line text-3xl" />
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-500">内容可能已下架，或链接有误</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to="/" className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
            返回首页
          </Link>
          <Link to="/category/all" className="whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600">
            浏览全部影视
          </Link>
        </div>
      </div>
    );
  }

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join(" / ") },
    { label: "类型", value: movie.type },
    { label: "年份", value: `${movie.year}` },
    { label: "片长", value: movie.duration },
    { label: "地区", value: movie.region },
  ];

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={`/category/${movie.category}`} className="transition-colors hover:text-primary-600">
          {movie.category === "movie" ? "电影" : "电视剧"}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{movie.title}</span>
      </nav>

      <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
        <div>
          <div className="relative w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={movie.cover}
              alt={`${movie.title} 海报`}
              title={`${movie.title} - 国产影视作品`}
              className="aspect-[2/3] h-full w-full object-cover object-top"
            />
            <button
              type="button"
              disabled
              title="演示站点，暂不支持播放"
              className="absolute inset-0 m-auto flex h-16 w-16 cursor-not-allowed items-center justify-center rounded-full bg-foreground-950/45 text-background-50"
            >
              <i className="ri-play-fill text-3xl" />
            </button>
          </div>

          <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-4">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                <i className="ri-star-fill text-accent-500" />
                综合评分
              </span>
              <span className="font-heading text-2xl font-bold text-accent-600">
                {movie.rating.toFixed(1)}
              </span>
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-background-300">
              <div
                className="h-full rounded-full bg-accent-500"
                style={{ width: `${(movie.rating / 10) * 100}%` }}
              />
            </div>
            <button
              type="button"
              disabled
              className="mt-4 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-md bg-secondary-300 py-2.5 text-sm font-medium text-secondary-800"
            >
              <i className="ri-play-circle-line text-lg" />
              播放（演示不可用）
            </button>
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {movie.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="rounded bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
                  {movie.category === "movie" ? "电影" : "电视剧"}
                </span>
                {movie.tags.map((tag) => (
                  <span key={tag} className="rounded bg-background-200 px-2.5 py-1 text-xs text-foreground-600">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={share}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-share-line" />
                分享
              </button>
            </div>
          </div>

          <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
            {movie.summary}
          </p>

          <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200 bg-background-100 p-4 sm:grid-cols-2 md:p-5">
            {infoRows.map((row) => (
              <div key={row.label} className="flex gap-3 text-sm">
                <span className="w-14 shrink-0 text-foreground-500">{row.label}</span>
                <span className="text-foreground-800">{row.value}</span>
              </div>
            ))}
          </div>

          <section className="mt-8">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-primary-500" />
              剧情介绍
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              {movie.synopsis}
            </p>
          </section>

          <section className="mt-8">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-primary-500" />
              用户留言
              <span className="text-sm font-normal text-foreground-500">（{demoComments.length} 条演示留言）</span>
            </h2>
            <div className="mt-4 flex flex-col gap-3">
              {demoComments.map((comment) => (
                <div key={comment.id} className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                    {comment.user.slice(0, 1)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-foreground-900">{comment.user}</span>
                      <span className="text-xs text-foreground-500">{comment.date}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-700">{comment.content}</p>
                    <div className="mt-2 flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-thumb-up-line" />
                      {comment.likes}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-foreground-400">
              留言功能为演示内容，暂不支持实时提交。
            </p>
          </section>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-14">
          <h2 className="mb-6 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="h-6 w-1.5 rounded-full bg-primary-500" />
            相关推荐
          </h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-3 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}